import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { createItineraryReportFinished, openItineraryReportDrawer } from '@capital-access/common/actions';
import { LocalizationService } from '@capital-access/common/localization';
import { DrawerOutput, DrawerPosition, DrawerType, FireflyDrawerService } from '@capital-access/firefly/components';
import { TemplateLibraryService } from '@capital-access/reporting/common';
import { LOCALIZATION_KEY } from '../../constants';
import { getValidItineraryLayout } from '../../core/utils/configurable-template.util';
import { getGroupedSavedSections } from '../../core/utils/section-settings.utils';
import { ItineraryReportDrawerComponent } from '../components/itinerary-report-drawer/itinerary-report-drawer.component';
import { SETTINGS_KEY } from '../constants';
import { CreateItineraryReportDto } from '../models/itinerary-report.models';
import { getDefaultSections, ItineraryReportLayout } from '../models/itinerary-report-sections';

@Injectable()
export class ItineraryDrawerEffects {
  openDrawer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openItineraryReportDrawer),
      withLatestFrom(this.localization.getLocalization(LOCALIZATION_KEY)),
      switchMap(
        ([
          { eventId, eventName, startDate, endDate, eventType, activities, configurableDisableReason },
          localization
        ]) => {
          const isStandardAvailable = !!eventId;

          return getValidItineraryLayout(this.store, this.templateService).pipe(
            switchMap(({ layout, templates }) => {
              let finalLayout = layout;

              // If Standard is not available and Configurable is not disabled, force Configurable
              if (!isStandardAvailable && !configurableDisableReason) {
                finalLayout = ItineraryReportLayout.Configurable;
              }

              // If the Configurable tab is disabled (activity/participant limit) but the saved default
              // was a Configurable template, fall back to Standard so the user lands on a usable tab.
              // The tab itself still renders as disabled with the existing popover tooltip.
              if (configurableDisableReason && finalLayout === ItineraryReportLayout.Configurable) {
                finalLayout = ItineraryReportLayout.Standard;
              }

              const itinerarySections = getGroupedSavedSections(SETTINGS_KEY, getDefaultSections());

              const drawer = this.drawerService.openDrawer(
                {
                  title: localization[`modifyItineraryReport`],
                  position: DrawerPosition.Right,
                  type: DrawerType.Form,
                  dataLayoutId: 'modify-itinerary-report-drawer'
                },
                ItineraryReportDrawerComponent,
                {
                  request: CreateItineraryReportDto.create({
                    eventId,
                    eventName,
                    startDate,
                    endDate,
                    eventType,
                    activities
                  }),
                  filteredSections: itinerarySections,
                  isConfigurableDisabled: !!configurableDisableReason,
                  configurableDisableReason,
                  layout: finalLayout,
                  wasConfigurableDefaultOverridden: finalLayout !== layout,
                  preloadedTemplates: templates
                }
              );

              return drawer.onClose().pipe(
                take(1),
                filter((output: DrawerOutput) => !output || !output['reportInProgress']),
                map(() => createItineraryReportFinished({ eventId }))
              );
            })
          );
        }
      )
    )
  );

  constructor(
    private actions$: Actions,
    private localization: LocalizationService,
    private drawerService: FireflyDrawerService,
    private store: Store,
    private templateService: TemplateLibraryService
  ) {}
}
